import * as styles from 'common/components/GenericModal/styles';
import {
  ActionButtons,
  Container,
  Content,
  Image,
} from 'common/components/GenericModal/styles';
import { BVSocial, BVSocialFormData } from 'containers/HomePageContainer/types/bvsocial';
import { BVSocialConfiguration } from 'containers/HomePageContainer/components/PageConfigurator/BVSocialModalContent/BVSocialConfiguration';
import {
  Box, Button, Divider,
} from '@mui/material';
import { DateTimePickerField } from 'common/components/DateTimePickerField';
import { Form, Formik } from 'formik';
import { Loader } from 'common/components/Loader';
import { LoadingButton } from '@mui/lab';
import { PageType } from 'common/enums/page-type.enum';
import { SourceSelect } from 'common/components/SourceSelect';
import { TextField } from 'common/components/TextField';
import { bvSocialWidgetSchema } from 'containers/HomePageContainer/components/PageConfigurator/BVSocialModalContent/bvsocial-widget.schema';
import { mapBVSocialCreationFormDataToRequest } from 'containers/HomePageContainer/components/PageConfigurator/BVSocialModalContent/mappers/map-bvsocial-creation-form-data-to-request';
import { mapBVSocialUpdateFormDataToRequest } from 'containers/HomePageContainer/components/PageConfigurator/BVSocialModalContent/mappers/map-bvsocial-update-form-data-to-request';
import { mapWidgetDataToFormInitialValues } from 'containers/HomePageContainer/components/PageConfigurator/BVSocialModalContent/mappers/map-widget-data-to-form-initial-values';
import { useCreateWidgetMutation } from 'containers/HomePageContainer/store/server/use-create-widget-mutation';
import { useDefaultInitialValues } from 'containers/HomePageContainer/components/PageConfigurator/BVSocialModalContent/hooks/use-default-initial-values';
import { useSearch } from 'containers/HomePageContainer/hooks/use-search';
import { useUpdateWidgetMutation } from 'containers/HomePageContainer/store/server/use-update-widget-mutation';
import { useWidget } from 'containers/HomePageContainer/store/server/use-widget';
import BVSocialImage from 'assets/widgets/thumbnails/BVSocial.png';
import CancelIcon from '@mui/icons-material/Cancel';
import React, { FC, useCallback } from 'react';
import SaveIcon from '@mui/icons-material/Save';

export interface BVSocialModalContentProps {
  closeModal: () => void;
  id?: string;
  pageType: PageType;
}

export const BVSocialModalContent: FC<BVSocialModalContentProps> = ({
  closeModal,
  id,
  pageType,
}) => {
  const defaultInitialValues = useDefaultInitialValues();
  const { culture } = useSearch();
  const { data: widget, isLoading } = useWidget<BVSocial>(id);
  const createMutation = useCreateWidgetMutation(closeModal);
  const updateMutation = useUpdateWidgetMutation(closeModal);

  const handleSubmit = useCallback((values: BVSocialFormData) => {
    if (id) {
      updateMutation.mutate({
        id,
        data: mapBVSocialUpdateFormDataToRequest(values),
      });
    } else {
      createMutation.mutate(mapBVSocialCreationFormDataToRequest(values, culture));
    }
  }, [id, culture, createMutation, updateMutation]);

  if (id && isLoading) {
    return <Container><Loader isLoading /></Container>;
  }

  return (
    <Formik
      initialValues={widget ? mapWidgetDataToFormInitialValues(widget) : defaultInitialValues}
      validationSchema={bvSocialWidgetSchema}
      onSubmit={handleSubmit}
    >
      {({ dirty }) => (
        <Form>
          <Container>
            <Content>
              <Box display="flex">
                <Image src={BVSocialImage} alt="BV Social widget" />
                <Box pl={3.75}>
                  <TextField name="name" label="Widget Name" fullWidth />
                  <Box display="flex">
                    <DateTimePickerField name="activeFrom" label="Valid from" sx={styles.dateTimePicker} />
                    <DateTimePickerField name="activeTo" label="Valid to" sx={styles.dateTimePicker} />
                    {pageType !== PageType.Plp && (
                      <SourceSelect name="sources" />
                    )}
                  </Box>
                </Box>
              </Box>
              <Divider sx={styles.divider} />
              <BVSocialConfiguration />
            </Content>
            <ActionButtons>
              <Button
                onClick={closeModal}
                size="large"
                variant="outlined"
                startIcon={<CancelIcon />}
              >
                Cancel
              </Button>
              <LoadingButton
                loading={createMutation.isLoading || updateMutation.isLoading}
                disabled={widget && !dirty}
                variant="contained"
                type="submit"
                size="large"
                startIcon={<SaveIcon />}
              >
                Save
              </LoadingButton>
            </ActionButtons>
          </Container>
        </Form>
      )}
    </Formik>
  );
};

BVSocialModalContent.defaultProps = {
  id: '',
};
